import { useEffect, useMemo, useState } from "react";
import { Search, Compass } from "lucide-react";
import api from "@/lib/api";
import EventCard from "@/components/EventCard";
import RegisterModal from "@/components/RegisterModal";
import PageHero from "@/components/PageHero";

const ARCHIVE_FILTERS = ["ALL", "EAST", "NORTH EAST", "NORTH", "CENTRAL", "SOUTH", "WEST", "HERITAGE", "HILL", "BEACH", "FOREST", "VILLAGE"];

export default function Events() {
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({ states: [], categories: [], landscapes: [] });
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [category, setCategory] = useState("");
  const [archiveFilter, setArchiveFilter] = useState("ALL");
  const [regEvent, setRegEvent] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/events/filters").then((r) => setFilters(r.data)).catch(() => {});
  }, []);

  const visible = useMemo(() => events.filter((e) => {
    if (q && !(e.name + e.destination + e.state).toLowerCase().includes(q.toLowerCase())) return false;
    if (state && e.state !== state) return false;
    if (category && e.category !== category) return false;
    return true;
  }), [events, q, state, category]);

  const byDate = (a, b) => (a.event_date || "9999-12-31").localeCompare(b.event_date || "9999-12-31");
  const upcoming = visible.filter((e) => e.status === "registration_open").sort(byDate);
  const comingSoon = visible.filter((e) => e.status === "coming_soon").sort(byDate);
  const archive = visible.filter((e) => e.status === "completed" || e.status === "closed").filter((e) => {
    if (archiveFilter === "ALL") return true;
    return e.region === archiveFilter || e.landscapes?.includes(archiveFilter);
  });

  return (
    <div data-testid="events-page">
      <PageHero eyebrow="UPCOMING EVENTS" title="Find Your Next Run." subtitle="Marathons, half marathons, trail runs and destination RUNSAFARI experiences across India."
        image="https://images.unsplash.com/photo-1590333748338-d629e4564ad9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxtYXJhdGhvbiUyMHJ1bm5lcnMlMjByYWNlJTIwc3RhcnR8ZW58MHx8fHwxNzg5MDY4MTkwfDA&ixlib=rb-4.1.0&q=85" />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="card-k flex flex-col gap-4 p-5 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="events-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search event, destination, state..." className="input-k !pl-11" />
          </div>
          <select data-testid="events-filter-state" value={state} onChange={(e) => setState(e.target.value)} className="input-k lg:w-56">
            <option value="">All States</option>
            {filters.states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="events-filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className="input-k lg:w-56">
            <option value="">All Categories</option>
            {filters.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="mt-12">
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white">Phase 1 — Registration Open</h2>
          {upcoming.length === 0 ? <p className="mt-6 text-slate-500">No open events match your filters right now.</p> : (
            <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {upcoming.map((e) => <EventCard key={e.slug} event={e} onRegister={setRegEvent} />)}
            </div>
          )}
        </div>

        {comingSoon.length > 0 && (
          <div className="mt-16">
            <p className="eyebrow">PHASE 2 — COMING SOON</p>
            <h2 className="mt-2 font-display text-3xl font-bold uppercase tracking-tight text-white">New Routes. New Destinations. New Adventures.</h2>
            <p className="mt-3 text-sm text-slate-400">SPORTIVE is continually developing new running experiences across India. Stay tuned.</p>
            <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {comingSoon.map((e) => <EventCard key={e.slug} event={e} onRegister={setRegEvent} />)}
            </div>
          </div>
        )}

        <div className="mt-20">
          <p className="eyebrow flex items-center gap-2"><Compass size={14} /> SPORTIVE EVENT ARCHIVE</p>
          <h2 className="mt-2 font-display text-3xl font-bold uppercase tracking-tight text-white">Where We've Run</h2>
          <div data-testid="archive-filters" className="mt-6 flex flex-wrap gap-2">
            {ARCHIVE_FILTERS.map((f) => (
              <button key={f} data-testid={`archive-filter-${f.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setArchiveFilter(f)}
                className={`rounded-full border px-4 py-1.5 font-mono text-[11px] tracking-[0.12em] transition-colors ${archiveFilter === f ? "border-brand-olive bg-brand-olive/15 text-brand-olive" : "border-brand-line text-slate-400 hover:border-brand-cyan hover:text-brand-cyan"}`}>
                {f}
              </button>
            ))}
          </div>
          {archive.length === 0 ? <p className="mt-6 text-slate-500">No archived events in this category yet — future projects coming soon.</p> : (
            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {archive.map((e) => <EventCard key={e.slug} event={e} onRegister={setRegEvent} />)}
            </div>
          )}
        </div>
      </section>

      {regEvent && <RegisterModal event={regEvent} onClose={() => setRegEvent(null)} />}
    </div>
  );
}
